import moment from 'moment';
import { logger } from './logger';

/**
 * Filter monthly data by date range using standardized moment.js parsing
 * @param {Object} monthlyData - Object with month keys (YYYY-MM format) and values
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Object} Filtered monthly data
 */
export const filterMonthlyDataByDateRange = (monthlyData, startDate, endDate) => {
  if (!monthlyData || typeof monthlyData !== 'object') {
    logger.warn('filterMonthlyDataByDateRange: Invalid monthlyData provided');
    return {};
  }
  
  if (!startDate || !endDate) {
    logger.warn('filterMonthlyDataByDateRange: Missing start or end date');
    return monthlyData;
  }

  const filteredData = {};
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  
  if (!startMoment.isValid() || !endMoment.isValid()) {
    logger.warn('filterMonthlyDataByDateRange: Invalid date format provided');
    return monthlyData;
  }
  
  const originalMonths = Object.keys(monthlyData).sort();
  logger.log(`📊 FILTERING MONTHLY DATA:`);
  logger.log(`  Date range: ${startDate} to ${endDate}`);
  logger.log(`  Available months: [${originalMonths.join(', ')}]`);
  
  Object.entries(monthlyData).forEach(([monthKey, value]) => {
    // Parse month key (format: "YYYY-MM")
    const monthMoment = moment(monthKey, 'YYYY-MM');
    
    if (!monthMoment.isValid()) {
      logger.warn(`filterMonthlyDataByDateRange: Invalid month key ${monthKey}`);
      return;
    }
    
    // Check if month falls within date range (inclusive)
    const isBetween = monthMoment.isBetween(startMoment, endMoment, 'month', '[]');
    logger.log(`    ${monthKey}: ${isBetween} (value: ${JSON.stringify(value)})`);
    
    if (isBetween) {
      filteredData[monthKey] = value;
    }
  });
  
  const filteredMonths = Object.keys(filteredData).sort();
  logger.log(`  🎯 Result: [${filteredMonths.join(', ')}] (${filteredMonths.length}/${originalMonths.length})`);
  
  return filteredData;
};

/**
 * Get available months in a date range
 * @param {Object} monthlyData - Object with month keys
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Array} Array of month keys that fall within the range
 */
export const getAvailableMonthsInRange = (monthlyData, startDate, endDate) => {
  if (!monthlyData || typeof monthlyData !== 'object') {
    return [];
  }
  
  const allMonths = Object.keys(monthlyData).sort();
  
  if (!startDate || !endDate) {
    return allMonths;
  }
  
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  
  if (!startMoment.isValid() || !endMoment.isValid()) {
    return allMonths;
  }
  
  return allMonths.filter(month => {
    const monthMoment = moment(month, 'YYYY-MM');
    return monthMoment.isValid() && monthMoment.isBetween(startMoment, endMoment, 'month', '[]');
  });
};

/**
 * Check data coverage and return analysis
 * @param {Array} businessData - Array of business objects with monthly data
 * @returns {Object} Data coverage analysis
 */
export const analyzeDataCoverage = (businessData) => {
  if (!Array.isArray(businessData) || businessData.length === 0) {
    return {
      totalMonths: 0,
      minMonth: null,
      maxMonth: null,
      monthsAvailable: []
    };
  }
  
  // Get all available months from all businesses
  const allMonths = new Set();
  
  businessData.forEach(business => {
    if (business?.data?.monthlyReviewCounts) {
      Object.keys(business.data.monthlyReviewCounts).forEach(month => {
        allMonths.add(month);
      });
    }
    if (business?.historicalData?.monthlyReviewCounts) {
      Object.keys(business.historicalData.monthlyReviewCounts).forEach(month => {
        allMonths.add(month);
      });
    }
  });
  
  const monthsArray = Array.from(allMonths).sort();
  
  return {
    totalMonths: monthsArray.length,
    minMonth: monthsArray.length > 0 ? monthsArray[0] : null,
    maxMonth: monthsArray.length > 0 ? monthsArray[monthsArray.length - 1] : null,
    monthsAvailable: monthsArray
  };
};

/**
 * Filter months array by date range with consistent logic
 * @param {Array} months - Array of month strings (YYYY-MM format)
 * @param {Object} dateRange - Object with startDate and endDate
 * @returns {Array} Filtered months array
 */
export const filterMonthsByDateRange = (months, dateRange) => {
  if (!Array.isArray(months) || months.length === 0) {
    logger.warn('filterMonthsByDateRange: No months to filter');
    return [];
  }
  
  if (!dateRange?.startDate || !dateRange?.endDate) {
    logger.log('filterMonthsByDateRange: No date range provided, returning all months');
    return months;
  }
  
  const startMoment = moment(dateRange.startDate);
  const endMoment = moment(dateRange.endDate);
  
  if (!startMoment.isValid() || !endMoment.isValid()) {
    logger.warn('filterMonthsByDateRange: Invalid date range provided', dateRange);
    return months;
  }
  
  logger.log(`🔍 FILTERING MONTHS:`);
  logger.log(`  Original months: [${months.join(', ')}]`);
  logger.log(`  Date range: ${dateRange.startDate} to ${dateRange.endDate}`);
  logger.log(`  Start moment: ${startMoment.format('YYYY-MM-DD')} (${startMoment.format('YYYY-MM')})`);
  logger.log(`  End moment: ${endMoment.format('YYYY-MM-DD')} (${endMoment.format('YYYY-MM')})`);
  
  const filtered = months.filter(month => {
    const monthMoment = moment(month, 'YYYY-MM');
    const isValid = monthMoment.isValid();
    const isBetween = monthMoment.isBetween(startMoment, endMoment, 'month', '[]');
    
    logger.log(`    ${month}: valid=${isValid}, between=${isBetween} (month: ${monthMoment.format('YYYY-MM')})`);
    
    return isValid && isBetween;
  });
  
  logger.log(`  🎯 Filtered result: [${filtered.join(', ')}]`);
  logger.log(`  📊 Original: ${months.length} → Filtered: ${filtered.length}`);
  
  return filtered;
};

/**
 * Log data coverage analysis for debugging
 * @param {Array} businessData - Array of business data
 * @param {Object} dateRange - Current date range
 */
export const logDataCoverage = (businessData, dateRange) => {
  const coverage = analyzeDataCoverage(businessData);
  
  logger.log('=== DATA COVERAGE ANALYSIS ===');
  logger.log(`Total months available: ${coverage.totalMonths}`);
  logger.log(`Date range: ${coverage.minMonth} to ${coverage.maxMonth}`);
  logger.log(`Months available: ${coverage.monthsAvailable.join(', ')}`);
  
  // **CRITICAL DEBUG INFO**: Show the actual issue
  if (coverage.totalMonths <= 3) {
    logger.warn(`🚨 LIMITED DATA DETECTED: Only ${coverage.totalMonths} months available!`);
    logger.warn(`This explains why all date ranges beyond 30 days return the same results.`);
    logger.warn(`Available months: ${coverage.monthsAvailable.join(', ')}`);
  }
  
  if (dateRange?.startDate && dateRange?.endDate) {
    const requestedStart = moment(dateRange.startDate).format('YYYY-MM');
    const requestedEnd = moment(dateRange.endDate).format('YYYY-MM');
    logger.log(`Requested range: ${requestedStart} to ${requestedEnd}`);
    
    const filteredMonths = filterMonthsByDateRange(coverage.monthsAvailable, dateRange);
    logger.log(`Filtered months: ${filteredMonths.length} (${filteredMonths.join(', ')})`);
    
    // Show the core problem
    if (filteredMonths.length === coverage.totalMonths) {
      logger.warn(`⚠️  IDENTICAL RESULTS: Filtered months (${filteredMonths.length}) equals total available (${coverage.totalMonths})`);
      logger.warn(`This means your date range includes ALL available data, explaining identical results.`);
    }
    
    // Warn if requested range extends beyond available data
    if (coverage.minMonth && requestedStart < coverage.minMonth) {
      logger.warn(`❌ Requested start date ${requestedStart} is before earliest available data ${coverage.minMonth}`);
    }
    if (coverage.maxMonth && requestedEnd > coverage.maxMonth) {
      logger.warn(`❌ Requested end date ${requestedEnd} is after latest available data ${coverage.maxMonth}`);
    }
  }
};